import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { Trophy, Gift, Copy, Users, Crown, Award, Star, Heart, Compass, Sparkles, MessageCircle, Share2 } from "lucide-react";

const ICONS = { sparkles: Sparkles, heart: Heart, compass: Compass, star: Star, "message-circle": MessageCircle, users: Users, crown: Crown, award: Award };

export default function MemberRewards() {
  const [d, setD] = useState(null);
  useSeo({ title: "Mes récompenses — PROXIreduc-28", description: "Vos points, badges et votre classement d'ambassadeur local.", noindex: true });

  useEffect(() => { api.get("/me/rewards").then((r) => setD(r.data)).catch(() => {}); }, []);

  const copy = () => { navigator.clipboard?.writeText(d?.referral?.link || ""); toast.success("Lien copié !"); };
  const share = () => {
    const txt = `Je profite des bons plans locaux d'Eure-et-Loir sur PROXIreduc-28 👉 ${d?.referral?.link || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  if (!d) return <p className="text-slate-500 text-sm" data-testid="rewards-loading">Chargement…</p>;

  return (
    <div className="space-y-6" data-testid="member-rewards-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Trophy className="text-amber-500" size={26} /> Mes récompenses
      </h1>

      {/* Points + stats */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-200 p-5" data-testid="rewards-points">
        <div className="flex items-end gap-2">
          <span className="font-heading text-4xl font-extrabold text-slate-900">{d.points}</span>
          <span className="text-slate-500 mb-1">points</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-white border border-slate-200 p-2">
            <div className="font-bold text-slate-900">{d.stats.referrals}</div><div className="text-[11px] text-slate-500">Filleuls</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-2">
            <div className="font-bold text-slate-900">{d.stats.reviews}</div><div className="text-[11px] text-slate-500">Avis</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-2">
            <div className="font-bold text-slate-900">{d.stats.favorites}</div><div className="text-[11px] text-slate-500">Favoris</div>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div data-testid="rewards-badges">
        <h2 className="font-heading text-lg font-bold mb-2">Mes badges</h2>
        <div className="flex flex-wrap gap-2">
          {d.badges.map((b) => {
            const Ic = ICONS[b.icon] || Sparkles;
            return (
              <span key={b.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                <Ic size={14} /> {b.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Parrainage */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="rewards-referral">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Gift size={18} className="text-[#E05638]" /> Parrainez vos proches</h2>
        <p className="mt-1 text-sm text-slate-600">Invitez vos amis à profiter des bons plans locaux. Chaque filleul vous fait grimper au classement des ambassadeurs !</p>
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={d.referral.link} data-testid="referral-link"
                 className="flex-1 min-w-0 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700" />
          <button onClick={copy} data-testid="referral-copy" className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold"><Copy size={14} /></button>
        </div>
        <button onClick={share} data-testid="referral-share" className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">
          <Share2 size={15} /> Partager sur WhatsApp
        </button>
      </div>

      {/* Classement */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="rewards-leaderboard">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Crown size={18} className="text-amber-500" /> Classement des ambassadeurs</h2>
        {d.my_rank && <p className="mt-1 text-sm text-slate-600">Votre rang : <b className="text-slate-900">#{d.my_rank}</b></p>}
        <ol className="mt-3 space-y-1.5">
          {d.leaderboard.length === 0 && <li className="text-sm text-slate-400">Soyez le premier ambassadeur !</li>}
          {d.leaderboard.map((l) => (
            <li key={l.rank} className={`flex items-center justify-between px-3 py-2 rounded-lg ${l.me ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
              <span className="flex items-center gap-2 text-sm">
                <span className={`w-6 h-6 grid place-items-center rounded-full text-xs font-bold ${l.rank <= 3 ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600"}`}>{l.rank}</span>
                {l.name}{l.me && <span className="text-[11px] text-amber-700 font-semibold">(vous)</span>}
              </span>
              <span className="text-sm text-slate-500">{l.referrals} filleul(s)</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
